import React, { memo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Link } from 'react-router-dom'

import { WidgetLayout } from '../../../lib/widget-layout'
import Text from '../../../ui/Text'
import { Button } from '../../../lib/button'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { MeasurementDevicesGetModel } from '../../../common/redux/api/web-api'
import { ChargersPagination } from '../../../lib/chargers-pagination'
import useText from '../../../common/hooks/useText'

export type CompanyMeasurementdevicesWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    measurementDevices: MeasurementDevicesGetModel[]
    removeMeasurementDevice: (measurementDevice: MeasurementDevicesGetModel) => void
    openMeasurementModal: (measurementDevice: MeasurementDevicesGetModel | null) => void
}

export const CompanyMeasurementdevicesWidget = memo(function CompanyMeasurementdevicesWidget({
    className,
    style,
    openMeasurementModal,
    measurementDevices,
    removeMeasurementDevice,
}: CompanyMeasurementdevicesWidgetProps): React.ReactElement {
    const text = useText()
    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize] = useState(20)
    const offset = currentPage * pageSize
    const handlePageClick = (selected: number) => {
        setCurrentPage(selected)
    }

    return (
        <WidgetLayout
            data-testid="table-md"
            className={cn(className)}
            style={style}
            title={text('lblMeasurementDevices')}
            headerButtons={[
                <Button
                    dataTestId="table-md-add-btn"
                    type="button"
                    code="btnAdd"
                    small
                    onClick={() => openMeasurementModal(null)}
                />,
            ]}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell data-testid="table-mdnumber-heading">
                            <Text code="lblSerialNumber"></Text>
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-mdname-heading">
                            <Text code="lblName"></Text>
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {measurementDevices?.slice(offset, offset + pageSize).map((device) => {
                        const mdBelongsToLBGroup = !!device?.loadBalancerGroupId
                        return (
                            <TableRow key={device?.id}>
                                <TableBodyCell data-testid="table-md-id-body">
                                    <Link
                                        to={`/measurementDevice/${device?.id}`}
                                        className="btn btn-link btn-xs"
                                        data-testid="table-md-chargePoint-name"
                                    >
                                        {device.serialNumber}
                                    </Link>
                                </TableBodyCell>
                                <TableBodyCell data-testid="table-md-name-body">{device.name}</TableBodyCell>

                                <TableBodyCell data-testid="table-md-removeBtn-body">
                                    <button
                                        disabled={mdBelongsToLBGroup}
                                        type="button"
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        title={
                                            mdBelongsToLBGroup ? text('msgMeasurementDeviceIsPartOfLBGroup') : undefined
                                        }
                                        onClick={() => removeMeasurementDevice(device)}
                                        className="btn btn-danger btn-xs btn-icon"
                                        data-testid="table-md-remove"
                                    >
                                        <span className="fa fa-remove"></span>
                                    </button>
                                </TableBodyCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            {measurementDevices?.length > 20 && (
                <ChargersPagination
                    setPageIndex={handlePageClick}
                    totalCount={measurementDevices?.length}
                    pageSize={pageSize}
                    pageIndex={currentPage}
                    displayedChargers={pageSize}
                />
            )}
            {!measurementDevices && <div className="shimmer-container shimmer-xsmall" data-testid="md-loading"></div>}
        </WidgetLayout>
    )
})
